import moment, { Moment } from 'moment'
import { useSetState } from 'ahooks'
import {
  Affix,
  Button,
  Card,
  Col,
  DateRangePicker,
  Error as ErrorComponent,
  ErrorAlert,
  Form,
  Info,
  Row,
  SEO,
  Title,
  TabPane,
  Tabs,
  DatePicker,
} from 'components/common'
import Layout from 'components/layouts/tow-column'
import {
  ReportDailySalesExportInput,
  ReportMonthlySalesExportInput,
  ReportErrorCode,
  ReportSalesExportInput,
  useReportDailySalesExportMutation,
  useReportMonthlySalesExportMutation,
  useReportSalesExportMutation,
} from 'lib/graphql/storeadmin'
import { useState } from 'react'
import MonthlyTable from 'pages/admin/home/monthly-table'
import { Scalars } from 'types/graphql'
import { downloadFile } from 'lib/common'

type FormValues = {
  dateRange?: Moment[]
  date?: Moment
  month?: Moment
}

type ErrorAlertState = {
  visible: boolean
  messages: string[]
}

export const Head = () => <SEO title="売上レポート" />

const IndexPage = () => {
  const [form] = Form.useForm()
  const [mutate, { error, loading }] = useReportSalesExportMutation({
    errorPolicy: 'all',
  })
  const [mutateDaily, { error: errorDaily, loading: loadingDaily }] =
    useReportDailySalesExportMutation({
      errorPolicy: 'all',
    })
  const [mutateMonthly, { error: errorMonthly, loading: loadingMonthly }] =
    useReportMonthlySalesExportMutation({
      errorPolicy: 'all',
    })
  // Schedule error useState
  const [state, setState] = useSetState<ErrorAlertState>({
    visible: false,
    messages: [],
  })
  const [activeTabKey, setActiveTabKey] = useState('date')

  const handleTabChange = (key) => {
    setActiveTabKey(key)
  }

  // 仮のバリデーション
  const handleApiResponse = (data) => {
    setState({ visible: false, messages: [] })
    let url
    if (data.reportMonthlySalesExport) {
      url = data.reportMonthlySalesExport.url
    } else if (data.reportDailySalesExport) {
      url = data.reportDailySalesExport.url
    }
    if (url) {
      downloadFile(url)
    } else {
      // エラー処理または適切なメッセージを表示するなど、urlが取得できなかった場合の処理を追加する
      console.error('URLが見つかりません')
    }
  }

  const onFinish = async (values: FormValues) => {
    try {
      let data //APIからのレスポンスデータを保持する変数
      // タブに基づいて処理を分岐
      if (activeTabKey === 'csv' && values.dateRange) {
        const dateFormat = 'YYYY-MM-DD'
        const reportSalesExportInput: ReportSalesExportInput = {
          startDate: values.dateRange[0].format(dateFormat),
          endDate: values.dateRange[1].format(dateFormat),
        }
        const { data } = await mutate({
          variables: {
            input: reportSalesExportInput,
          },
        })
        const userErrors = data.reportSalesExport.reportUserErrors
        if (userErrors.length > 0) {
          const errorMessages = userErrors.map(({ code }) => {
            switch (code) {
              case ReportErrorCode.RangeInvalid:
                return '集計期間が１年を超えています。'
              case ReportErrorCode.NotFound:
                return '出力可能なデータが存在しませんでした。'
              default:
                return '利用予定表出力時に予期せぬエラーが発生しました。'
            }
          })
          setState({ visible: true, messages: errorMessages })
        } else {
          setState({ visible: false, messages: [] })
          const url = data.reportSalesExport.url
          downloadFile(url)
        }
      } else if (activeTabKey === 'date' && values.date) {
        // 日付での処理
        const dateFormat = 'YYYY-MM-DD'
        const aggregationDate = values.date.format(dateFormat)
        const reportDailySalesExportInput: ReportDailySalesExportInput = {
          aggregationDate,
        }
        // 日報指定APIの呼び出し
        const response = await mutateDaily({
          variables: {
            input: reportDailySalesExportInput,
          },
        })
        data = response.data // APIのレスポンスをdataにセット
      } else if (activeTabKey === 'month' && values.month) {
        const monthFormat = 'YYYY-MM'
        const aggregationMonth = values.month.format(monthFormat)

        const reportMonthlySalesExportInput: ReportMonthlySalesExportInput = {
          aggregationMonth,
        }
        // 月報APIの呼び出し
        const response = await mutateMonthly({
          variables: {
            input: reportMonthlySalesExportInput,
          },
        })
        data = response.data
      }

      if (data) {
        handleApiResponse(data)
      }
    } catch (e) {
      console.log(e)
      setState({
        visible: true,
        messages: ['サーバーで予期せぬエラーが発生しました。'],
      })
    }
  }

  const onCloseAlert = () => {
    setState({ visible: false })
  }

  if (errorDaily || errorMonthly) {
    return ErrorComponent.render(errorDaily || errorMonthly)
  }

  return (
    <>
      <Form
        form={form}
        component="form"
        requiredMark="optional"
        onFinish={onFinish}
      >
        <Layout title="売上レポート">
          <Layout.Section size="small">
            {state.visible && (
              <ErrorAlert
                onCloseAlert={onCloseAlert}
                alertDescription={state.messages}
              />
            )}

            <Layout.Item>
              <Card shadow>
                <Tabs accessKey={activeTabKey} onChange={handleTabChange}>
                  <TabPane tab="日次" key="date">
                    <Form.Item
                      label={<Title level={5}>日付指定</Title>}
                      name="date"
                      rules={[{ required: false, message: '集計日は必須です' }]}
                      noStyle
                    >
                      <DatePicker />
                    </Form.Item>
                  </TabPane>
                  <TabPane tab="月次" key="month">
                    <Form.Item
                      label={<Title level={5}>月指定</Title>}
                      name="month"
                      rules={[{ required: false, message: '集計月は必須です' }]}
                      noStyle
                    >
                      <DatePicker picker="month" placeholder="月を選択" />
                    </Form.Item>
                  </TabPane>
                  <TabPane tab="CSV" key="csv">
                    <Form.Item
                      name="dateRange"
                      label={
                        <Info
                          title={
                            '開始月刻は終了刻月より前である必要があります。例：2022-01 〜 22-02 '
                          }
                        >
                          <Title level={5}>集計期間</Title>
                        </Info>
                      }
                      rules={[
                        { required: true, message: '集計期間は必須です' },
                        () => ({
                          validator(_, value) {
                            return Promise.resolve()
                          },
                        }),
                      ]}
                      required
                    >
                      <DateRangePicker picker="date" />
                    </Form.Item>
                  </TabPane>
                </Tabs>
              </Card>
            </Layout.Item>

            <Layout.Item>
              <Affix offsetTop={120}>
                <Row>
                  <Col span={18}></Col>
                  <Col span={6}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loadingDaily || loadingMonthly || loading}
                      block
                    >
                      売上を出力する
                    </Button>
                  </Col>
                </Row>
              </Affix>
            </Layout.Item>
          </Layout.Section>
        </Layout>
      </Form>
    </>
  )
}

export default IndexPage
